import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, useRef, useEffect } from 'react';
import {
  Box,
  Checkbox,
  Container,
  FormControlLabel,
  Typography,
  Button,
  Stack,
  TextField,
} from '@mui/material';
import * as Sentry from '@sentry/react';
import { visuallyHidden } from '@mui/utils';
import { usePostHog } from '../components/widgets/PostHogProvider';
import { BrandBackdrop } from '../components/brand/BrandBackdrop';
import { FadeIn } from '../components/ui/FadeIn';
import { ExternalUrls } from '../lib/constants';
import { getFirstTouchAttribution } from '../utils/firstTouch';

export const Route = createFileRoute('/get-started')({
  component: GetStartedPage,
});

// Mirrors the in-app OnboardingModal enums so booth leads are segmented the
// same way as signed-up users. VALUES must equal the generated FundingType /
// Discipline enums — labels are free to stay marketing-friendly. Exported so
// get-started.test.tsx can assert that equality against @neumind/shared rather
// than trusting this comment: these values ride the /signup handoff and the
// `POST /leads` body, and the signup route silently DROPS any value the enum
// doesn't recognise, so a drifted spelling loses the answer with no error
// anywhere. Hardcoded rather than imported so the marketing bundle doesn't pull
// in the generated SDK — the test carries the coupling instead.
export const FUNDING_OPTIONS = [
  { value: 'private', label: 'Private / Case-managed' },
  { value: 'public', label: 'NHS / Publicly funded' },
  { value: 'third_sector', label: 'Third sector / Charity' },
  { value: 'mixed', label: 'Mixed' },
] as const;

// Ordering is deliberate (VER-260729 walk): Case Management first — the
// BABICM/case-manager persona is the funnel's primary audience — then OT,
// Psychology, SLT, with the remaining disciplines after. Order is presentation
// only; the enum-alignment tests compare sorted value sets, so reordering here
// is safe.
export const DISCIPLINE_OPTIONS = [
  { value: 'case_management', label: 'Case Management' },
  { value: 'occupational_therapy', label: 'Occupational Therapy' },
  { value: 'psychology', label: 'Psychology' },
  { value: 'speech_language', label: 'Speech & Language Therapy' },
  { value: 'physiotherapy', label: 'Physiotherapy' },
  { value: 'nursing', label: 'Nursing' },
  { value: 'rehab_support', label: 'Rehab / Support' },
  { value: 'other', label: 'Other' },
] as const;

// vr6t fork-collapse: 3 steps, no done-state fork — Continue on the details
// step IS the create-account action; demo/explore are demoted text links.
const TOTAL_STEPS = 3; // discipline, funding, details

// Consent-independent durable lead capture (ISS-260608-rkr3). A form submission
// is a deliberate first-party action (not analytics tracking), so this fires
// regardless of the cookie-consent choice — unlike PostHog, which is
// consent-gated. Points at our own backend `POST /leads` (the lead lands in
// Postgres and is stitched to the account at signup — ISS-260608-5fbe), not a
// third-party Sheet.
//
// Derived from VITE_API_URL by default: the deployed www build is handed the
// shared `neumind/{env}/frontend/env` secret (see .github/actions/
// deploy-static-sites), which carries VITE_API_URL but has no
// VITE_LEADS_API_URL entry — so hard-requiring the latter would have shipped a
// funnel that silently captured nothing until someone added a master secret.
// VITE_LEADS_API_URL still wins if set (e.g. pointing capture at a different
// host). Empty-by-default keeps an unconfigured build a safe no-op.
//
// Build-time gated by VITE_LEADS_CAPTURE_ENABLED (ISS-260717-qjq1): www,
// backend, and CORS deploy through separate paths with no ordering guarantee,
// so an unrelated static-sites deploy from main would otherwise activate the
// POST against an env whose backend/CORS aren't wired yet (404 + a Sentry
// warning per submission). Only the exact string 'true' enables — absent,
// empty, or malformed values all mean dormant. The check sits ABOVE the
// explicit override so VITE_LEADS_API_URL can't bypass the gate. Deliberately
// not a PostHog flag: PostHog is consent-gated, capture is consent-independent.
const resolveLeadsApiUrl = (): string => {
  if (import.meta.env.VITE_LEADS_CAPTURE_ENABLED !== 'true') return '';
  const explicit = import.meta.env.VITE_LEADS_API_URL;
  if (explicit) return explicit;
  const apiUrl = import.meta.env.VITE_API_URL;
  return apiUrl ? `${String(apiUrl).replace(/\/+$/, '')}/leads` : '';
};

const LEADS_API_URL = resolveLeadsApiUrl();

// Keep the mutable checkbox copy and its evidence version together. Changing
// the wording requires a version bump so stored consent remains demonstrable.
export const MARKETING_CONSENT = {
  copy: 'Keep me posted on Neumind updates',
  version: 'get-started-v1',
  source: 'www_get_started',
} as const;

const LEAD_CAPTURE_ATTEMPTS = 3;
const MAX_RETRY_WINDOW_MS = 30_000;
// Two blind waits are at least 4s + 8s, so attempt three starts after the
// endpoint's 10-second medium window even when Retry-After is hidden by CORS.
const BASE_RETRY_DELAY_MS = 4_000;

const wait = (delayMs: number) => new Promise<void>((resolve) => setTimeout(resolve, delayMs));

function retryAfterMs(response: Response): number | null {
  // Cross-origin callers only see this when the API exposes Retry-After via
  // CORS. The current server does not, so blind backoff remains the fallback.
  const value = response.headers?.get?.('Retry-After');
  if (!value) return null;

  const seconds = Number(value);
  if (Number.isFinite(seconds) && seconds >= 0) return seconds * 1_000;

  const date = Date.parse(value);
  if (Number.isNaN(date)) return null;
  return Math.max(0, date - Date.now());
}

async function postLead(body: Record<string, unknown>): Promise<void> {
  const startedAt = Date.now();

  for (let attempt = 0; attempt < LEAD_CAPTURE_ATTEMPTS; attempt += 1) {
    let response: Response;
    try {
      response = await fetch(LEADS_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        keepalive: true,
      });
    } catch {
      Sentry.captureMessage('Durable lead capture failed', {
        level: 'warning',
        tags: { surface: 'get_started', reason: 'network_error' },
      });
      return;
    }

    if (response.ok) return;

    const canRetry = response.status === 429 && attempt < LEAD_CAPTURE_ATTEMPTS - 1;
    if (!canRetry) {
      Sentry.captureMessage('Durable lead capture failed', {
        level: 'warning',
        tags: {
          surface: 'get_started',
          reason: response.status === 429 ? 'rate_limited' : 'http_error',
          status: String(response.status),
        },
      });
      return;
    }

    const blindDelay = BASE_RETRY_DELAY_MS * 2 ** attempt * (1 + Math.random() * 0.5);
    const elapsed = Date.now() - startedAt;
    const remaining = Math.max(0, MAX_RETRY_WINDOW_MS - elapsed);
    await wait(Math.min(retryAfterMs(response) ?? blindDelay, remaining));
  }
}

/** Segmented step indicator — mirrors the in-app OnboardingModal. */
function StepIndicator({ currentIndex }: { currentIndex: number }) {
  return (
    <Stack
      direction="row"
      spacing={1}
      justifyContent="center"
      sx={{ mb: 3 }}
      role="group"
      aria-label={`Step ${currentIndex + 1} of ${TOTAL_STEPS}`}
    >
      {Array.from({ length: TOTAL_STEPS }, (_, i) => {
        const isActive = i === currentIndex;
        return (
          <Box
            key={i}
            aria-current={isActive ? 'step' : undefined}
            sx={{
              width: isActive ? 24 : 8,
              height: 8,
              borderRadius: 4,
              bgcolor: i <= currentIndex ? 'primary.main' : 'grey.300',
              transition: 'width 0.3s ease, background-color 0.3s ease',
              '@media (prefers-reduced-motion: reduce)': {
                transition: 'none',
              },
            }}
          />
        );
      })}
    </Stack>
  );
}

const isValidEmail = (e: string) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e);

export function GetStartedPage() {
  const { capture, identify } = usePostHog();

  const [step, setStep] = useState(0); // 0 discipline · 1 funding · 2 details
  const [discipline, setDiscipline] = useState('');
  const [funding, setFunding] = useState('');
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  // PECR: marketing consent is opt-in — never pre-ticked.
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [touched, setTouched] = useState(false);
  // Cross-origin handoff to the dashboard can take a beat on a conference
  // phone connection — flip a local flag so the tap visibly "took" (qhnq):
  // Continue disables and relabels to "Opening…" while the browser navigates.
  const [navigating, setNavigating] = useState(false);
  const firstNameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  // y18b: step swaps replace the card content silently for screen-reader and
  // keyboard users — move focus to the incoming step's heading (skip the
  // initial mount so page focus order is undisturbed on load).
  const stepHeadingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (step > 0) stepHeadingRef.current?.focus();
  }, [step]);

  const handleDiscipline = (value: string) => {
    setDiscipline(value);
    setStep(1);
  };

  const handleFunding = (value: string) => {
    setFunding(value);
    setStep(2);
  };

  const detailsValid = firstName.trim().length > 0 && isValidEmail(email);

  const handleDetailsSubmit = () => {
    setTouched(true);
    if (!detailsValid) {
      // Move focus to the first invalid field so its label + error are read
      // (errors otherwise surface silently on submit for keyboard/SR users).
      if (firstName.trim().length === 0) firstNameRef.current?.focus();
      else emailRef.current?.focus();
      return;
    }
    // Durable lead record: identify the person by email (PII lives here),
    // funnel event stays non-PII.
    identify(email.trim(), {
      firstName: firstName.trim(),
      discipline,
      fundingType: funding,
      lead_source: 'booth_get_started',
    });
    capture('booth_lead_qualified', {
      discipline,
      funding_type: funding,
      has_email: true,
    });
    // Consent-independent durable capture (fire-and-forget; never blocks UX).
    // JSON to our backend; CORS for this origin is configured server-side.
    // marketingConsent rides the same submission (explicit mailing-list opt-in,
    // upgrade-only server-side); attribution is the tab's first touch (UTMs
    // only exist on the landing URL — recorded by main.tsx, read here).
    // postLead sends keepalive: the create-account navigation follows
    // immediately (vr6t collapse), so the request must survive the unload.
    if (LEADS_API_URL) {
      const attribution = getFirstTouchAttribution();
      void postLead({
        firstName: firstName.trim(),
        email: email.trim(),
        discipline,
        fundingType: funding,
        source: 'booth_get_started',
        marketingConsent,
        marketingConsentVersion: MARKETING_CONSENT.version,
        marketingConsentSource: MARKETING_CONSENT.source,
        ...(attribution ? { attribution } : {}),
      });
    }
    // vr6t: Continue IS the create-account action — no fork step. Programmatic
    // nav (not an anchor) because validation gates it above.
    handleRoute('create_account');
    setNavigating(true);
    window.location.assign(createAccountHref);
  };

  const handleRoute = (
    destination: 'create_account' | 'book_demo' | 'explore',
  ) => {
    capture('booth_lead_routed', {
      destination,
      discipline,
      funding_type: funding,
    });
  };

  // Full handoff to the dashboard signup: name + the ICP answers, so a
  // qualified lead never re-answers what they already told us. The signup
  // route validates these against the generated enums (junk drops silently).
  const handoffParams = new URLSearchParams({ given_name: firstName.trim() });
  if (email.trim()) handoffParams.set('email', email.trim());
  if (discipline) handoffParams.set('discipline', discipline);
  if (funding) handoffParams.set('funding_type', funding);
  const createAccountHref = `${ExternalUrls.dashboardStart}?${handoffParams.toString()}`;

  return (
    <Box
      sx={{
        // Center within the space BELOW the sticky AppBar (56px mobile / 64px
        // desktop) — plain 100vh overflows by the header height and pushes the
        // card down the page.
        minHeight: { xs: 'calc(100vh - 56px)', md: 'calc(100vh - 64px)' },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: { xs: 2, md: 3 },
        background:
          'linear-gradient(135deg, rgba(77, 101, 255, 0.05) 0%, rgba(14, 165, 223, 0.05) 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <BrandBackdrop
        mesh="light"
        constellation
        constellationCorner="top-right"
        constellationOpacity={0.45}
      />
      <Container maxWidth="xs" sx={{ position: 'relative', zIndex: 1 }}>
        <FadeIn direction="up" duration={600}>
          <Box
            sx={{
              bgcolor: 'background.paper',
              borderRadius: 4,
              p: { xs: 3, md: 4 },
              boxShadow:
                '0 16px 40px -8px rgba(27,58,107,0.14), 0 4px 12px -2px rgba(27,58,107,0.08)',
              border: '1px solid rgba(27,58,107,0.08)',
              textAlign: 'center',
            }}
          >
            {/* y18b: polite announcement of step progress for screen readers —
                persists across step swaps so changes are read. */}
            <Box role="status" sx={visuallyHidden}>
              Step {step + 1} of {TOTAL_STEPS}
            </Box>
            <StepIndicator currentIndex={step} />

            {/* Step 1 — Discipline (pill chips) */}
            {step === 0 && (
              <Stack spacing={1.5} alignItems="center">
                <Typography
                  variant="h5"
                  fontWeight={700}
                  tabIndex={-1}
                  ref={stepHeadingRef}
                  sx={{ '&:focus': { outline: 'none' } }}
                >
                  What&apos;s your discipline?
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  We&apos;ll tailor what you see to how you work.
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                    justifyContent: 'center',
                    pt: 1,
                  }}
                >
                  {DISCIPLINE_OPTIONS.map((opt) => (
                    <Button
                      key={opt.value}
                      variant="outlined"
                      onClick={() => handleDiscipline(opt.value)}
                      sx={{
                        py: 1,
                        px: 2,
                        minHeight: 44, // phone-first touch target (BABICM)
                        textTransform: 'none',
                        borderRadius: 5,
                        borderColor: 'grey.300',
                        color: 'text.primary',
                        fontSize: '0.875rem',
                        '&:hover': {
                          borderColor: 'primary.main',
                          bgcolor: 'primary.50',
                        },
                      }}
                    >
                      {opt.label}
                    </Button>
                  ))}
                </Box>
              </Stack>
            )}

            {/* Step 2 — Funding (full-width buttons) */}
            {step === 1 && (
              <Stack spacing={1.5} sx={{ width: '100%', maxWidth: 400, mx: 'auto' }}>
                <Typography
                  variant="h5"
                  fontWeight={700}
                  tabIndex={-1}
                  ref={stepHeadingRef}
                  sx={{ '&:focus': { outline: 'none' } }}
                >
                  How are your clients typically funded?
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This helps us point you at the right setup.
                </Typography>
                <Stack spacing={1} sx={{ pt: 1 }}>
                  {FUNDING_OPTIONS.map((opt) => (
                    <Button
                      key={opt.value}
                      variant="outlined"
                      onClick={() => handleFunding(opt.value)}
                      sx={{
                        py: 1.25,
                        minHeight: 44, // phone-first touch target (BABICM)
                        textTransform: 'none',
                        borderRadius: 2,
                        borderColor: 'grey.300',
                        color: 'text.primary',
                        '&:hover': {
                          borderColor: 'primary.main',
                          bgcolor: 'primary.50',
                        },
                      }}
                    >
                      {opt.label}
                    </Button>
                  ))}
                </Stack>
              </Stack>
            )}

            {/* Step 3 — Details */}
            {step === 2 && (
              <Stack spacing={2} sx={{ width: '100%', maxWidth: 400, mx: 'auto' }}>
                <Typography
                  variant="h5"
                  fontWeight={700}
                  tabIndex={-1}
                  ref={stepHeadingRef}
                  sx={{ '&:focus': { outline: 'none' } }}
                >
                  Your details
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Where should we send your next steps?
                </Typography>
                {/* rpva item 5: name/email/consent are one visual group — the
                    reserved helper-text line under each field already provides
                    breathing room, so the inner Stack is tight. The outer
                    Stack's spacing (group → Continue) tested right — keep. */}
                <Stack spacing={0.5}>
                  <TextField
                    label="First name"
                    inputRef={firstNameRef}
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    fullWidth
                    autoComplete="given-name"
                    inputProps={{ maxLength: 80 }}
                    error={touched && firstName.trim().length === 0}
                    helperText={
                      touched && firstName.trim().length === 0
                        ? 'Please enter your first name'
                        : ' '
                    }
                  />
                  <TextField
                    label="Email"
                    type="email"
                    inputRef={emailRef}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    fullWidth
                    autoComplete="email"
                    inputProps={{ maxLength: 255 }}
                    error={touched && !isValidEmail(email)}
                    helperText={
                      touched && !isValidEmail(email)
                        ? 'Please enter a valid email'
                        : ' '
                    }
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleDetailsSubmit();
                    }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={marketingConsent}
                        onChange={(e) => setMarketingConsent(e.target.checked)}
                        size="small"
                      />
                    }
                    label={
                      <Typography variant="body2" color="text.secondary">
                        {MARKETING_CONSENT.copy}
                      </Typography>
                    }
                    sx={{ alignSelf: 'flex-start' }}
                  />
                </Stack>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleDetailsSubmit}
                  disabled={navigating}
                  sx={{ py: 1.25 }}
                >
                  {navigating ? 'Opening…' : 'Continue'}
                </Button>
                {/* Demoted secondary routes (vr6t): visible text links, still
                    measured via booth_lead_routed. */}
                <Stack spacing={0} alignItems="center">
                  <Button
                    component="a"
                    href={ExternalUrls.calendly}
                    target="_blank"
                    rel="noopener"
                    onClick={() => handleRoute('book_demo')}
                    color="inherit"
                    sx={{ textTransform: 'none', color: 'text.secondary' }}
                  >
                    Prefer to talk first? Book a demo
                  </Button>
                  <Button
                    component={Link}
                    to="/solutions/professionals"
                    onClick={() => handleRoute('explore')}
                    color="inherit"
                    sx={{ textTransform: 'none', color: 'text.secondary' }}
                  >
                    Or explore what Neumind does
                  </Button>
                </Stack>
              </Stack>
            )}

          </Box>
        </FadeIn>
      </Container>
    </Box>
  );
}
